import type { Ref } from 'vue'
import { toRef } from 'vue'
import type {
    CreateShapeEnquiryEngagementRingCollectionExistingCustomerMutation,
    CreateShapeEnquiryEngagementRingCollectionNewCustomerMutation,
    CustomerRegistrationInput,
    EnquiryEngagementRingCollectionInput,
} from '@/graphql/api'
import { type CatalogueEngagementRingInputFragment } from '@/graphql/api'
import { getUri } from '@/helpers/getCurrentUri'
import { getCommissionJunctionInput } from '@/helpers/commission-junction'
import { useHandleEnquiryResponse } from '@/composables/enquiry/useHandleEnquiryResponse'
import { createResponseAndPath, type ResponseAndPath } from '@/helpers/errors'
import { getCatalogueEngagementRingFilterInput } from '@/domain/product/ring/engagement'
import { useIsAdmin } from '@/components/app/isAdmin'
import { useCreateShapeEnquiryEngagementRingCollectionExistingCustomerMutation } from '@/graphql/api/product/rings/engagement/shape-enquiry/createShapeEnquiryEngagementRingCollectionExistingCustomer.mutation.generated.ts'
import { useCreateShapeEnquiryEngagementRingCollectionNewCustomerMutation } from '@/graphql/api/product/rings/engagement/shape-enquiry/createShapeEnquiryEngagementRingCollectionNewCustomer.mutation.generated.ts'

export function useEngagementRingCreateShapeEnquiry(
    ring: Readonly<
        Ref<CatalogueEngagementRingInputFragment | undefined | null>
    >,
    customerRegistrationInput: Readonly<
        Ref<CustomerRegistrationInput | undefined>
    >
): {
    createShapeEnquiryExistingCustomer: () => void
    createShapeEnquiryNewCustomer: () => void
    fetchingCreateShapeEnquiry: Readonly<Ref<boolean, boolean>>
    shapeEnquiryErrors: readonly [
        ResponseAndPath<CreateShapeEnquiryEngagementRingCollectionExistingCustomerMutation>,
        ResponseAndPath<CreateShapeEnquiryEngagementRingCollectionNewCustomerMutation>,
    ]
} {
    const enquiryInput = ():
        | EnquiryEngagementRingCollectionInput
        | undefined => {
        if (!ring.value) {
            return undefined
        }

        const catalogueEngagementRingFilter =
            getCatalogueEngagementRingFilterInput(ring.value)

        return {
            catalogueEngagementRingFilter,
            commissionJunction: getCommissionJunctionInput(),
            uri: getUri(),
        }
    }

    const { handleEnquiryResponse } = useHandleEnquiryResponse()
    const isAdmin = useIsAdmin()
    // Existing customer
    const existingCustomerResponse =
        useCreateShapeEnquiryEngagementRingCollectionExistingCustomerMutation()

    const {
        executeMutation: mutationExistingCustomer,
        fetching: fetchingExistingCustomer,
    } = existingCustomerResponse

    const createShapeEnquiryExistingCustomer = () => {
        const input = enquiryInput()

        if (!input) {
            return
        }

        mutationExistingCustomer({ input }).then(({ data, error }) => {
            if (data) {
                handleEnquiryResponse({
                    data: {
                        customer: undefined,
                    },
                    error,
                })
            }
        })
    }

    // New customer
    const newCustomerResponse =
        useCreateShapeEnquiryEngagementRingCollectionNewCustomerMutation()

    const {
        executeMutation: mutationNewCustomer,
        fetching: fetchingNewCustomer,
    } = newCustomerResponse

    const createShapeEnquiryNewCustomer = () => {
        const input = enquiryInput()
        const customer = customerRegistrationInput.value

        if (!input || !customer) {
            return
        }

        mutationNewCustomer({
            input,
            customer,
            isConsultant: isAdmin.value,
        }).then(
            ({ data, error }) =>
                data &&
                handleEnquiryResponse({
                    data: data?.createEnquiryEngagementRingPreferAnotherShapeNewCustomer,
                    error,
                })
        )
    }

    const shapeEnquiryErrors = [
        createResponseAndPath(
            existingCustomerResponse,
            'createEnquiryEngagementRingPreferAnotherShapeExistingCustomer'
        ),
        createResponseAndPath(
            newCustomerResponse,
            'createEnquiryEngagementRingPreferAnotherShapeNewCustomer'
        ),
    ] as const

    const fetchingCreateShapeEnquiry = toRef(
        () => fetchingExistingCustomer.value || fetchingNewCustomer.value
    )

    return {
        createShapeEnquiryExistingCustomer,
        createShapeEnquiryNewCustomer,
        fetchingCreateShapeEnquiry,
        shapeEnquiryErrors,
    }
}
